import { useEffect, useState } from 'react';

import { Button, ListItemIcon, ListItemText, Menu, SxProps, Theme } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { KeyboardArrowDown } from '@mui/icons-material';

import { useAppContext } from '@/context';

import CustomTypography from '../UI/Typography/CustomTypography';
import { colors } from '../UI/colors';

import { reasonForVisitStyles } from './ReasonForVisit.styles';
import { BrokenTooth } from './BrokenTooth';
import { CheckUp } from './CheckUp';
import { DentalImplants } from './DentalImplants';
import { Dentures } from './Dentures';
import { Emergency } from './Emergency';
import { MottoAligners } from './MottoAligners';

interface IReasonForVisitProps {
	reasonForVisit: string;
	handleReasonForVisitChange: (event: { target: { value: string } }) => void;
	id: string;
	className?: string;
}

export const REASON_FOR_VISITS: Array<{
	label: string;
	value: string;
	icon: JSX.Element;
}> = [
	{
		label: 'Dentures',
		value: 'dentures',
		icon: <Dentures />,
	},
	{
		label: 'Dental Implants',
		value: 'implants',
		icon: <DentalImplants />,
	},
	{
		label: 'Motto™ Clear Aligners',
		value: 'mottoaligners',
		icon: <MottoAligners />,
	},
	{
		label: 'Emergency',
		value: 'emergency',
		icon: <Emergency />,
	},
	{
		label: 'Broken Tooth/Tooth Pain',
		value: 'brokentooth-toothpain',
		icon: <BrokenTooth />,
	},
	{
		label: 'Other/Check Up',
		value: 'other',
		icon: <CheckUp />,
	},
];

export const ReasonForVisit = ({ reasonForVisit, handleReasonForVisitChange, id, className }: IReasonForVisitProps) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const { config } = useAppContext();
	const isMotto = (config.name || '').toLowerCase().includes('motto');

	// Auto-select Motto Clear Aligners when brand is Motto and no selection exists
	useEffect(() => {
		if (isMotto && !reasonForVisit) {
			handleReasonForVisitChange({ target: { value: 'mottoaligners' } });
		}
	}, [isMotto, reasonForVisit, handleReasonForVisitChange]);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleMenuItemClick = (_event: React.MouseEvent<HTMLElement>, value: string) => {
		handleReasonForVisitChange({ target: { value } });
		handleClose();
	};

	// Filter options based on brand - only show Motto option when brand is Motto
	const availableReasons = isMotto
		? REASON_FOR_VISITS.filter((reason) => reason.value === 'mottoaligners')
		: REASON_FOR_VISITS;

	const selectedReason = availableReasons.find((reason) => reason.value === reasonForVisit);

	return (
		<>
			<Button
				id={id}
				aria-controls={open ? 'reason-menu' : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : 'false'}
				onClick={handleClick}
				endIcon={
					<KeyboardArrowDown
						sx={{ color: colors.text.primary, '&.MuiSvgIcon-root': { fontSize: '1.5rem' } }}
					/>
				}
				sx={reasonForVisitStyles.reasonSelectButton}
				className={className}
				fullWidth
			>
				<CustomTypography variant="bodyS">
					{selectedReason ? selectedReason.label : 'Reason for visit'}
				</CustomTypography>
			</Button>
			<Menu
				id="reason-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				slotProps={{
					paper: {
						sx: reasonForVisitStyles.reasonMenuPaper,
					},
				}}
				MenuListProps={{
					sx: reasonForVisitStyles.reasonMenuList,
					'aria-labelledby': id,
				}}
			>
				{availableReasons.map((reason) => {
					const isSelected = reason.value === reasonForVisit;
					const menuItemSx = isSelected
						? ([reasonForVisitStyles.reasonMenuItem, reasonForVisitStyles.reasonMenuItemSelected] as const)
						: reasonForVisitStyles.reasonMenuItem;
					return (
						<MenuItem
							key={reason.value}
							selected={isSelected}
							onClick={(event) => handleMenuItemClick(event, reason.value)}
							id={reason.value}
							sx={menuItemSx as SxProps<Theme>}
						>
							<ListItemIcon>{reason.icon}</ListItemIcon>
							<ListItemText primary={reason.label}>
								<CustomTypography variant="bodyM">{reason.label}</CustomTypography>
							</ListItemText>
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
};
